import type { DutyAnalyticsCardKind, DutyAnalyticsLayout, DutyAnalyticsMetric, DutyAnalyticsRange } from '@pams/shared';
import { formatCount } from '@/lib/analyticsReport';

/**
 * ─────────────────────────────────────────────────────────────────────────────────
 *  [P7C §2] EVERY WORD ON THE DUTY ANALYTICS PAGE AND ITS DRILL — but one.
 *
 *  ⚠️ THE ONE EXCEPTION IS THE PREVIEW BADGE, which sits on ONE line of `index.tsx` holding its own
 *  literal, so deleting that line deletes the badge; `noScore.test.ts` pins it there.
 *
 *  ⚠️ NO SCORE, RANK, RATE OR PERCENTAGE (§2d), AND NO WORD FOR A SUPERVISORY SEAT (U1). The one
 *  allowed literal is the best mapping card's title, which appears exactly once below — the scan
 *  removes it once and then refuses every other occurrence of the word.
 *
 *  ⚠️ ONE WORD, "duties", THROUGHOUT. The table says "Duties asked" and the delegation sentence says
 *  "duties asked of" — the same thing, called the same thing.
 * ─────────────────────────────────────────────────────────────────────────────────
 */

/** A count of duties, singular at one. */
const duties = (count: number): string => `${formatCount(count)} ${count === 1 ? 'duty' : 'duties'}`;

const listOf = (items: readonly string[]): string =>
  new Intl.ListFormat('en-GB', { style: 'long', type: 'conjunction' }).format(items);

/** What a row is, for each layout's cards and tables. */
export const DUTY_ANALYTICS_NOUN: Record<DutyAnalyticsLayout, { one: string; many: string }> = {
  COMMISSIONERATE: { one: 'division', many: 'divisions' },
  DIVISION: { one: 'unit', many: 'units' },
};

/** ⚠️ EXHAUSTIVE OVER THE METRICS, so a tenth figure is a compile error here rather than a blank header. */
export const DUTY_ANALYTICS_METRIC_LABEL: Record<DutyAnalyticsMetric, string> = {
  ASKED: 'Duties asked',
  NAMED: 'Named',
  DELEGATED: 'Delegated downward',
  DECLARED_SHORT: 'Short-falls declared',
  LEFT_OPEN: 'Shares left open',
  REACHED: 'Reached',
  ABSENT: 'Absent',
  MARKED_SELF: 'Marked by the officer themselves',
  MARKED_ON_BEHALF: 'Marked on the officer’s behalf',
};

/** The same figure inside a sentence — the accessible name of a count that opens its rows. */
const METRIC_PHRASE: Record<DutyAnalyticsMetric, string> = {
  ASKED: 'duties asked',
  NAMED: 'named',
  DELEGATED: 'delegated downward',
  DECLARED_SHORT: 'short-falls declared',
  LEFT_OPEN: 'shares left open',
  REACHED: 'reached',
  ABSENT: 'absent',
  MARKED_SELF: 'marked by the officer themselves',
  MARKED_ON_BEHALF: 'marked on the officer’s behalf',
};

/**
 * What each figure counts. ⚠️ Exhaustive, so the notes cannot fall behind the table, and plain: a note
 * an operator cannot read in one breath is a note nobody reads.
 *
 * [amended P7C.1 §3] Read WORD FOR WORD by the per-card and per-section info dialogs (`figureInfo.ts`,
 * `components/FigureInfo.tsx`) as well as by the hidden notes card — so the short-fall pair below is the same two
 * sentences wherever it appears, and `DutyAnalytics.test.tsx` asserts them as literals.
 */
const METRIC_NOTE: Record<DutyAnalyticsMetric, string> = {
  ASKED: 'Positions asked of a division or unit — people only; a vehicle is counted nowhere on this page.',
  NAMED: 'Positions with an officer named into them.',
  DELEGATED:
    'Positions a division handed down to a unit beneath it, counted against the division that handed them down.',
  DECLARED_SHORT:
    'Shares closed with fewer names than were asked, with a written reason. Whoever declared it has answered.',
  LEFT_OPEN: 'Shares on a duty that has ended, neither filled nor closed with a reason. Nobody answered them.',
  REACHED: 'Named officers recorded as having reached the duty, or as having stood down from it.',
  ABSENT: 'Named officers recorded absent, or with no arrival recorded by the time the duty ended.',
  MARKED_SELF: 'Taps an officer made on their own duty.',
  MARKED_ON_BEHALF: 'Taps entered for an officer by someone else on the duty.',
};

export const DUTY_ANALYTICS_COPY = {
  title: 'Duty analytics',
  rangeLabel: 'Range',
  ranges: { 1: 'Last day', 7: 'Last 7 days', 30: 'Last 30 days' } satisfies Record<DutyAnalyticsRange, string>,
  /** [P7C.1 §5] The fourth choice beside the presets, and its two dates. The refusals are `@pams/shared`'s. */
  customRange: 'Custom',
  customRangeLabel: 'Custom range',
  customFrom: 'From',
  customTo: 'To',

  /** [P7C.1 §4] A division's own page, read from the commissionerate. */
  asViewTitle: (division: string) => `Duty analytics — ${division}`,
  viewingAs: (division: string) =>
    `${division}’s own page, as the division sees it: its units, its own total, and the commissionerate’s totals beside it.`,
  asViewMismatch: 'This division’s page could not be shown. Open it again from the commissionerate’s page.',
  divisionPageLabel: (division: string) => `${division} — open the division’s own page`,
  updated: (window: string, time: string) => `${window} · Updated ${time} IST`,
  updating: 'Loading this range…',
  refreshFailed: (window: string) =>
    `${window} · This range could not be refreshed; the figures are the last ones read`,
  loadingPage: 'Loading duty analytics',
  loadingSection: (title: string) => `Loading ${title.toLowerCase()}`,
  errorTitle: 'Duty analytics could not be loaded',
  errorDescription: 'The server did not answer. Nothing about your access has changed.',
  retry: 'Try again',
  sectionFailure: 'This section could not be loaded.',

  cardsLabel: 'Headlines',
  cards: {
    MOST_ASKED: 'Most duties asked',
    BEST_MAPPING: 'Best mapping rate',
    ASKED_AND_NAMED: 'Asked and named',
    OPEN_AND_DECLARED: 'Left open and declared short',
  } satisfies Record<DutyAnalyticsCardKind, string>,
  nothingAsked: 'No duties were asked in this range.',
  nobodyNamed: (noun: string) => `No ${noun} named anyone in this range.`,
  /** U4, in counts: who was compared, and the floor they had to clear. */
  floor: (noun: { one: string; many: string }, askedAtLeast: number, qualifying: number, considered: number) =>
    `Chosen among the ${formatCount(qualifying)} ${qualifying === 1 ? noun.one : noun.many} asked for at least ${duties(askedAtLeast)}, the median of the ${formatCount(considered)} asked for any.`,
  dutiesAsked: 'duties asked',
  ofNamedJoin: 'of',
  named: 'named',
  sharesLeftOpen: 'shares left open',
  shortfallsDeclared: 'short-falls declared',

  columns: DUTY_ANALYTICS_METRIC_LABEL,
  countLabel: (metric: DutyAnalyticsMetric, value: number, rowName: string) =>
    `${formatCount(value)} ${METRIC_PHRASE[metric]} — ${rowName}`,
  unavailable: '—',
  unavailableColumns: (labels: readonly string[]) =>
    `${listOf(labels)} could not be loaded. The other columns are complete.`,

  divisionsTitle: 'Divisions',
  unitsTitle: 'Units',
  divisionUnitsTitle: (division: string) => `${division} and its units`,
  divisionHeader: 'Division',
  unitHeader: 'Unit',
  askedThroughHeader: 'Asked through',
  allDivisions: 'All divisions',
  divisionTotal: (division: string) => `${division} total`,
  yourDivision: 'Your division',
  /** U2 — why the commissionerate's row has no links. */
  commissionerateReason:
    'Every division together, for comparison. It counts other divisions’ work, so its figures do not open from here.',
  commissionerateOwnRow: (name: string) => `${name} — asked of the commissionerate itself`,
  divisionKeptRow: (name: string) => `${name} — kept by the division itself`,
  divisionItselfSecondary: 'Asked of the division itself',
  unknownUnit: 'A unit no longer on record',
  noRows: 'Nothing was asked of anyone in this range.',
  noUnits: 'No unit was asked for anything in this range.',
  pickerLabel: 'Division',
  pickerPlaceholder: 'All divisions',

  delegationTitle: 'Delegation',
  delegationSentence: (asked: number, handedDown: number, name: string) =>
    handedDown === 0
      ? `None of the ${duties(asked)} asked of ${name} was handed down.`
      : `Of the ${duties(asked)} asked of ${name}, ${formatCount(handedDown)} ${handedDown === 1 ? 'was' : 'were'} handed down to its units.`,
  delegationHandedDown: 'Handed down to a unit',
  delegationKept: 'Kept by the division',
  delegationDetails: 'Show the figures',
  delegationHandedDownHeader: 'Handed down',
  delegationKeptHeader: 'Not handed down',
  delegationNothingAsked: 'Nothing was asked in this range, so nothing could be handed down.',
  /** ⚠️ The exemption from "every non-zero count opens its rows", stated where it applies. */
  delegationExemption: 'The numbers beside the bars are for reading; the same counts open their rows from the table.',

  markingsTitle: 'Markings',
  markingsSentence:
    'Two counts, never added together. Each counts taps only — a marking the app adds alongside another is not counted again.',

  notesTitle: 'What each figure counts',
  notes: METRIC_NOTE,
  /** [P7C.1 §3] An info button's name and its dialog's title — the card or section it explains, quoted. */
  infoLabel: (title: string) => `What “${title}” shows`,
  ordersNote:
    'Every issued order whose duty dates fall in the range, including orders filed after they ran. Cancelled orders and drafts are not counted.',
  layoutNote:
    'Divisions are counted by the division a position was asked through; a division’s own page counts by the unit each position is addressed to, so one division can read differently on the two.',

  /** The drill page. */
  rowsParent: 'Duty analytics',
  rowsInAll: (figure: number) => `${formatCount(figure)} in all`,
  rowsEmpty: 'Nothing to list for this figure in this range.',
  rowsErrorTitle: 'These rows could not be loaded',
  ownTotal: 'Your division’s total',
} as const;
